import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, Alert } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { collection, collectionGroup, getCountFromServer } from 'firebase/firestore';
import { db } from '../../../../utils/firebase';
import { useAuth } from '../../../../utils/context/AuthContext';
import PageContainer from '../../../../components/container/PageContainer';
import Breadcrumb from '../../../../layouts/full-layout/breadcrumb/Breadcrumb';

const Analytics = () => {
    const user = useAuth();
    const isAdmin = user.storedUser && user.storedUser.type === 'admin';
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isAdmin) { setLoading(false); return; }
        (async () => {
            try {
                const [users, recipes, pantry, grocery, plans, likes] = await Promise.all([
                    getCountFromServer(collection(db, 'users')),
                    getCountFromServer(collectionGroup(db, 'posts')),
                    getCountFromServer(collectionGroup(db, 'pantries')),
                    getCountFromServer(collectionGroup(db, 'groceries')),
                    getCountFromServer(collectionGroup(db, 'plans')),
                    getCountFromServer(collectionGroup(db, 'likes')),
                ]);
                setStats({
                    users: users.data().count,
                    recipes: recipes.data().count,
                    pantry: pantry.data().count,
                    grocery: grocery.data().count,
                    plans: plans.data().count,
                    likes: likes.data().count,
                });
            } catch (e) {
                console.error('analytics error:', e);
                setError('Could not load analytics. Make sure the latest Firestore rules are deployed.');
            }
            setLoading(false);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cards = stats ? [
        { label: 'Users', value: stats.users, icon: 'users', color: '#4caf50' },
        { label: 'Recipes', value: stats.recipes, icon: 'book-open', color: '#2196f3' },
        { label: 'Pantry items', value: stats.pantry, icon: 'box', color: '#ff9800' },
        { label: 'Grocery items', value: stats.grocery, icon: 'shopping-cart', color: '#9c27b0' },
        { label: 'Meal plans', value: stats.plans, icon: 'calendar', color: '#00bcd4' },
        { label: 'Recipe likes', value: stats.likes, icon: 'heart', color: '#e91e63' },
        {
            label: 'Avg recipes / user',
            value: stats.users ? (stats.recipes / stats.users).toFixed(1) : '0',
            icon: 'trending-up',
            color: '#607d8b',
        },
    ] : [];

    return (
        <PageContainer title="Analytics - Spenza" description="Community analytics for Spenza admins.">
            <Breadcrumb title="Analytics" subtitle="Spenza" />

            {!isAdmin && (
                <Alert severity="warning">This page is only available to admins.</Alert>
            )}

            {isAdmin && loading && (
                <Box display="flex" justifyContent="center" sx={{ mt: 6 }}><CircularProgress /></Box>
            )}

            {isAdmin && error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {isAdmin && !loading && stats && (
                <>
                    <Grid container spacing={3}>
                        {cards.map((c) => (
                            <Grid item xs={6} md={3} key={c.label}>
                                <Card>
                                    <CardContent>
                                        <Box
                                            sx={{
                                                width: 44, height: 44, borderRadius: '12px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                bgcolor: `${c.color}22`, color: c.color, mb: 1.5,
                                            }}
                                        >
                                            <FeatherIcon icon={c.icon} width="22" />
                                        </Box>
                                        <Typography variant="h2" sx={{ fontWeight: 700 }}>{c.value}</Typography>
                                        <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                        Deeper insights (most-used ingredients over time, expiry &amp; waste,
                        ingredient utilisation) are coming next.
                    </Typography>
                </>
            )}
        </PageContainer>
    );
};

export default Analytics;
